import React, { useState } from 'react';
import { Clock, RefreshCw, MessageCircle, X } from 'lucide-react';
import './UpdateComposer.css';

interface UpdateComposerProps {
  activityName: string;
  onPost: (type: 'late' | 'change' | 'note', text: string, durationMinutes?: number) => Promise<void>;
  onClose: () => void;
}

const LATE_OPTIONS = [15, 30, 45, 60];

const UpdateComposer: React.FC<UpdateComposerProps> = ({ activityName, onPost, onClose }) => {
  const [mode, setMode] = useState<'choose' | 'late' | 'change' | 'note'>('choose');
  const [text, setText] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (mode === 'late') {
        await onPost('late', text, duration || 15);
      } else if (mode === 'change') {
        await onPost('change', text);
      } else {
        await onPost('note', text);
      }
      onClose();
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="uc-overlay" onClick={onClose}>
      <div className="uc-sheet" onClick={e => e.stopPropagation()}>
        <div className="uc-handle" />
        <div className="uc-header">
          <span className="uc-activity-name">{activityName}</span>
          <button className="uc-close" onClick={onClose}><X size={16} /></button>
        </div>

        {mode === 'choose' && (
          <div className="uc-options">
            <button className="uc-option" onClick={() => setMode('late')}>
              <Clock size={16} />
              <div className="uc-option-text">
                <span className="uc-option-label">Heads up</span>
                <span className="uc-option-desc">Let everyone know you're running late</span>
              </div>
            </button>
            <button className="uc-option" onClick={() => setMode('change')}>
              <RefreshCw size={16} />
              <div className="uc-option-text">
                <span className="uc-option-label">We're changing this</span>
                <span className="uc-option-desc">Something changed about this activity</span>
              </div>
            </button>
            <button className="uc-option" onClick={() => setMode('note')}>
              <MessageCircle size={16} />
              <div className="uc-option-text">
                <span className="uc-option-label">From the day</span>
                <span className="uc-option-desc">Share a note, tip, or moment</span>
              </div>
            </button>
          </div>
        )}

        {mode === 'late' && (
          <div className="uc-form">
            <span className="uc-form-label">How late?</span>
            <div className="uc-duration-row">
              {LATE_OPTIONS.map(mins => (
                <button
                  key={mins}
                  className={`uc-duration-chip ${duration === mins ? 'active' : ''}`}
                  onClick={() => setDuration(mins)}
                >
                  {mins >= 60 ? `${mins / 60}hr` : `${mins}m`}
                </button>
              ))}
            </div>
            <input
              className="uc-text-input"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Any detail? (optional)"
              maxLength={200}
            />
            <button className="uc-submit" onClick={handleSubmit} disabled={!duration || isSubmitting}>
              {isSubmitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        )}

        {(mode === 'change' || mode === 'note') && (
          <div className="uc-form">
            <span className="uc-form-label">
              {mode === 'change' ? 'What changed?' : 'Your note'}
            </span>
            <textarea
              className="uc-textarea"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={mode === 'change' ? 'Bakery was closed, going to the cathedral instead' : 'Met the owner, great person...'}
              maxLength={500}
              rows={3}
              autoFocus
            />
            <button className="uc-submit" onClick={handleSubmit} disabled={!text.trim() || isSubmitting}>
              {isSubmitting ? 'Posting...' : 'Post'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateComposer;
